import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog } from "radix-ui";
import { Bug } from "lucide-react";
import {
  Button,
  cn,
  dialogBodyClass,
  dialogContentClass,
  dialogOverlayClass,
  formatRelative,
  useAuth,
  useToast,
} from "@ozpanel/ui";

// "Report a bug" (owner, 2026-11-05) — ONE component for the three consoles. It files POST /bug-reports
// (internal/modules/bugreports): the console and the reporter come from the caller's own login, the page from
// where they are. The cluster's operator sees every report; where a copy goes is the cluster's setting. The
// "Your reports" tab shows each report's delivery, its ozpanel ticket and the replies, and lets the reporter answer.

export interface BugReport {
  id: string;
  created_at: string;
  console: string;
  path: string;
  module?: string;
  reporter_role: string;
  reporter_email?: string;
  mailbox?: string;
  tenant_id?: number;
  contact: boolean;
  title: string;
  description: string;
  severity: string;
  has_screenshot: boolean;
  delivery: "pending" | "sent" | "failed" | "refused" | "local";
  destination?: string;
  attempts: number;
  last_error?: string;
  sent_at?: string;
  ticket_id?: string;
  ticket_status?: string;
  replies?: { at: string; from: string; body: string }[] | null;
}

const field = "w-full rounded-lg border border-line2 bg-bg px-3 py-2 text-[13.5px] text-fg outline-none focus:border-accent";

/** Where a report went, in words. */
export function deliveryText(r: BugReport): string {
  switch (r.delivery) {
    case "local":
      return "kept with this panel's operator";
    case "pending":
      return "being sent";
    case "failed":
      return `not delivered yet — retrying (${r.last_error ?? "the destination did not answer"})`;
    case "refused":
      return `refused by the destination, not retried (${r.last_error ?? "no reason given"})`;
    case "sent":
      return r.ticket_id ? `sent to ozpanel — ticket ${r.ticket_id} (${r.ticket_status || "open"})` : `sent (${r.destination})`;
  }
  return r.delivery;
}

async function fileToB64(f: File): Promise<string> {
  const buf = new Uint8Array(await f.arrayBuffer());
  let s = "";
  for (let i = 0; i < buf.length; i += 0x8000) s += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(s);
}

export function ReportBugButton({ admin = false, module, className }: { admin?: boolean; module?: string; className?: string }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "flex min-h-11 w-full items-center justify-center gap-2 rounded-[9px] border border-line2 bg-surface text-[13.5px] font-medium text-fg hover:border-[var(--ozp-accent-line)]",
          className,
        )}
      >
        <Bug size={15} /> Report a bug
      </button>
      {open && <ReportBugDialog admin={admin} module={module} onClose={() => setOpen(false)} />}
    </>
  );
}

export function ReportBugDialog({ admin, module, onClose }: { admin: boolean; module?: string; onClose: () => void }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [tab, setTab] = React.useState<"new" | "mine">("new");
  const [title, setTitle] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [severity, setSeverity] = React.useState("normal");
  const [contact, setContact] = React.useState(false);
  const [diagnostics, setDiagnostics] = React.useState(false);
  const [shot, setShot] = React.useState<File | null>(null);
  const [busy, setBusy] = React.useState(false);
  const mine = useQuery({
    queryKey: ["bug-reports"],
    queryFn: () => client.apiFetch<{ items: BugReport[] }>("/bug-reports"),
    enabled: tab === "mine",
  });

  const submit = async () => {
    if (shot && shot.size > 2 * 1024 * 1024) {
      notify({ kind: "error", title: "The screenshot is larger than 2 MB" });
      return;
    }
    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        title,
        description,
        severity,
        contact,
        path: window.location.pathname + window.location.search,
        module: module ?? "",
        diagnostics: admin && diagnostics,
      };
      if (shot) {
        body.screenshot_b64 = await fileToB64(shot);
        body.screenshot_type = shot.type;
      }
      await client.apiFetch("/bug-reports", {
        method: "POST",
        body: JSON.stringify(body),
      });
      notify({
        kind: "success",
        title: "Thank you — the report is filed",
        body: "You can follow it under Your reports.",
      });
      void qc.invalidateQueries({ queryKey: ["bug-reports"] });
      setTitle("");
      setDescription("");
      setShot(null);
      setTab("mine");
    } catch (e) {
      notify({
        kind: "error",
        title: "Not filed",
        body: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className={dialogOverlayClass} />
        <Dialog.Content className={dialogContentClass}>
          <div className={cn(dialogBodyClass, "p-5")}>
            <Dialog.Title className="text-[15px] font-medium">Report a bug</Dialog.Title>
            <Dialog.Description className="mt-1 text-[12.5px] text-muted">
              What went wrong, and what you expected. This page's address is included.
            </Dialog.Description>
            <div className="mt-3 flex gap-2 text-[13px]">
              {(["new", "mine"] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  className={cn(
                    "rounded-md px-2.5 py-1",
                    tab === t ? "bg-surface2 font-medium text-fg" : "text-muted hover:text-fg",
                  )}
                >
                  {t === "new" ? "New report" : "Your reports"}
                </button>
              ))}
            </div>
            {tab === "new" ? (
              <div className="mt-3 space-y-3">
                <label className="block">
                  <span className="text-[12.5px] text-muted">Title</span>
                  <input className={field} value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
                </label>
                <label className="block">
                  <span className="text-[12.5px] text-muted">What happened</span>
                  <textarea className={field} rows={5} value={description} onChange={(e) => setDescription(e.target.value)} />
                </label>
                <div className="flex flex-wrap items-end gap-3">
                  <label className="block">
                    <span className="text-[12.5px] text-muted">How bad</span>
                    <select className={cn(field, "w-40")} value={severity} onChange={(e) => setSeverity(e.target.value)}>
                      <option value="low">Low — a small thing</option>
                      <option value="normal">Normal</option>
                      <option value="high">High — blocks my work</option>
                    </select>
                  </label>
                  <label className="block min-w-0 flex-1">
                    <span className="text-[12.5px] text-muted">Screenshot (optional, PNG or JPEG, ≤ 2 MB)</span>
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      className="block w-full text-[12.5px]"
                      onChange={(e) => setShot(e.target.files?.[0] ?? null)}
                    />
                  </label>
                </div>
                <label className="flex items-start gap-2 text-[13px]">
                  <input type="checkbox" className="mt-0.5" checked={contact} onChange={(e) => setContact(e.target.checked)} />
                  <span>You may contact me about this at my sign-in address</span>
                </label>
                {admin && module && (
                  <label className="flex items-start gap-2 text-[13px]">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={diagnostics}
                      onChange={(e) => setDiagnostics(e.target.checked)}
                    />
                    <span>Attach the {module} module's health as the panel measures it now</span>
                  </label>
                )}
              </div>
            ) : (
              <MyReports reports={mine.data?.items} loading={mine.isLoading} />
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-2 border-t border-line px-5 py-3.5">
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
            {tab === "new" && (
              <Button variant="accent" disabled={busy || !title.trim() || !description.trim()} onClick={() => void submit()}>
                {busy ? "Filing…" : "File the report"}
              </Button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function MyReports({ reports, loading }: { reports?: BugReport[]; loading: boolean }) {
  if (loading) return <p className="mt-3 text-[13px] text-muted">Loading…</p>;
  if (!reports?.length) return <p className="mt-3 text-[13px] text-muted">No reports yet.</p>;
  return (
    <ul className="mt-3 max-h-[50vh] space-y-2 overflow-y-auto">
      {reports.map((r) => (
        <ReportItem key={r.id} r={r} />
      ))}
    </ul>
  );
}

/** One report: what it says, where it went, the replies, and a reply box once ozpanel has a ticket. */
export function ReportItem({ r, showReporter = false }: { r: BugReport; showReporter?: boolean }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [reply, setReply] = React.useState("");
  const send = async () => {
    try {
      await client.apiFetch(`/bug-reports/${r.id}/replies`, {
        method: "POST",
        body: JSON.stringify({ body: reply }),
      });
      setReply("");
      notify({ kind: "success", title: "Reply sent" });
      void qc.invalidateQueries({ queryKey: ["bug-reports"] });
    } catch (e) {
      notify({
        kind: "error",
        title: "Not sent",
        body: e instanceof Error ? e.message : String(e),
      });
    }
  };
  return (
    <li className="rounded-lg border border-line px-3 py-2">
      <button type="button" className="flex w-full items-start justify-between gap-3 text-left" onClick={() => setOpen(!open)}>
        <span className="min-w-0">
          <span className="block truncate text-[13.5px] font-medium">{r.title}</span>
          <span className="block text-[12px] text-muted">
            {formatRelative(r.created_at)} · {r.severity}
            {showReporter && ` · ${r.console} · ${r.mailbox || r.reporter_email || r.reporter_role}`}
          </span>
        </span>
        <span className={cn("shrink-0 text-[12px]", r.delivery === "failed" || r.delivery === "refused" ? "text-warn" : "text-muted")}>
          {deliveryText(r)}
        </span>
      </button>
      {open && (
        <div className="mt-2 space-y-2 text-[13px]">
          <p className="whitespace-pre-line">{r.description}</p>
          <p className="text-[12px] text-muted">
            Page {r.path || "—"}
            {r.module ? ` · module ${r.module}` : ""}
            {r.has_screenshot ? " · screenshot attached" : ""}
          </p>
          {(r.replies ?? []).map((x, i) => (
            <div key={i} className="rounded-md bg-surface2 px-3 py-2">
              <div className="text-[11.5px] text-muted">
                {x.from} · {formatRelative(x.at)}
              </div>
              <div className="whitespace-pre-line">{x.body}</div>
            </div>
          ))}
          {r.ticket_id && r.ticket_status !== "closed" && (
            <div className="flex gap-2">
              <input
                className={field}
                placeholder="Reply on the ticket"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
              />
              <Button variant="accent" size="sm" disabled={!reply.trim()} onClick={() => void send()}>
                Send
              </Button>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
